import { useNavigate } from 'react-router-dom';
import { useReveal } from '@/hooks/useReveal';
import PageLoader from '@/components/PageLoader';
import SubPageHero from '@/components/SubPageHero';
import Button from '@/components/ui/Button';
import { GUIDE_ICON } from '@/components/GuideIcons';
import HeroCrumbs from '@/components/HeroCrumbs';
import c from './Company.module.scss';
import x from './CompanyCertifications.module.scss';
import s from './ProductEvsG200.module.scss'; // [2026-10-11 사용자] 설치사업(환경 소속)=다크 재테마용(환경 페이지 공통 모듈)
import t from './ProductInstruments.module.scss'; // 다크 카드(t.buyCard)·시안 라인아이콘(t.cardIcon)
import w from './SafetyEnv.module.scss'; // [2026-10-22 사용자] 설치사업(환경) 본문 화이트화. ※설치안내(InstallProcess 등 c/x)는 그대로 유지
const IcW = ({ name }: { name: string }) => <div className={w.iconWrap}>{GUIDE_ICON[name]}</div>;
function SoonW({ label }: { label: string }) {
  return (
    <div className={w.soon} role="img" aria-label={`${label} 준비중`}>
      <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>
      <span>{label} · 준비중</span>
    </div>
  );
}

// [2026-10-08 사용자] 문서(v7)로 추가된 설치안내/설치사업 섹션의 '글자만 있는' 카드에
//   절제된 플랫 라인 SVG 아이콘(공용 GUIDE_ICON 재사용). 카드 h3 위 x.cardIcon 슬롯.
//   아이콘 의미 매핑은 각 카드 주제에 맞춤(서류=doc·현장=building·계측=gauge·통신=signal 등).
const Ic = ({ name }: { name: string }) => <div className={x.cardIcon}>{GUIDE_ICON[name]}</div>;

// ============================================================================
//  설치안내(도입 프로세스) + 설치사업  (기획서 v7 5-1·5-2·5-3 + 4-3) — B안(부장 확정 후)
//  [2026-10-08 사용자] '지금 해' → 설치안내를 '도입 프로세스'로 재정의(/install/*), /business/install을 '설치사업'으로 정리.
//    /guide(배출·방지시설 안내)는 콘텐츠 보존한 채 환경 하위로 이동(메뉴만). 라이트 테마(정보·지원 페이지).
//    대표제목(슬로건)·섹션 순서·연결 이름은 문서 그대로. 실자료 미보유=준비중.
// ============================================================================

const HERO = '/images/hero/hero-install.jpg'; // [2026-10-11 사용자] 설치안내(도입 프로세스) 히어로 이미지 교체 = 산업단지+IoT망(target images). 구 company/subpage-hero에서 변경
const HERO_ENV = '/images/hero/hero-guide.jpg'; // 설치사업(/business/install)=환경 메뉴 소속 → 환경 통일 히어로(다크). [[evs-hero-uniformity]]
const Reveal = ({ className = '', children }: { className?: string; children: React.ReactNode }) => {
  const ref = useReveal<HTMLDivElement>();
  return <div className={`${className} anim-scroll`} ref={ref}>{children}</div>;
};
// 라이트(설치안내) placeholder
function Soon({ label }: { label: string }) {
  return (
    <div className={x.soon} role="img" aria-label={`${label} 준비중`}>
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>
      <span>{label} · 준비중</span>
    </div>
  );
}
// 다크(설치사업) 전용 헬퍼 — 환경 페이지와 테마 일치
const IcD = ({ name }: { name: string }) => <div className={t.cardIcon}>{GUIDE_ICON[name]}</div>;
function SoonD({ label }: { label: string }) {
  return (
    <div className={s.soon} role="img" aria-label={`${label} 준비중`}>
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>
      <span>{label} · 준비중</span>
    </div>
  );
}

// ── 5-1 도입 절차 ──
export const InstallProcess = () => {
  const nav = useNavigate();
  // [2026-10-22 사용자] '너무 단조로움' → 그라데이션 STEP 카드로 재디자인(참고=2번째 이미지).
  //   각 단계 색(g1→g2)·설명(d) 추가. 색은 단계 진행 레인보우(주황→그린). 설명으로 가독성 보강.
  const steps = [
    { l: '상담', i: 'phone', d: '도입 목적·현장 조건 상담', g1: '#f7a823', g2: '#ef6f2e' },
    { l: '서류검토', i: 'doc', d: '인허가·시설 서류 확인', g1: '#f45c8c', g2: '#e23b86' },
    { l: '현장실사', i: 'building', d: '설치 위치·전원·통신 점검', g1: '#b24bd6', g2: '#8e3fd0' },
    { l: '설계·견적', i: 'solution', d: '장비 구성·포설·견적 제안', g1: '#7b5be0', g2: '#5f45d6' },
    { l: '설치', i: 'gateway', d: '장비 설치·배선·시공', g1: '#4e74f0', g2: '#3b5be0' },
    { l: '연동', i: 'signal', d: '계측·통신·정상전송 확인', g1: '#2f9bf0', g2: '#2477d6' },
    { l: '교육', i: 'action', d: '사용법·장애 대응 교육', g1: '#17b5c6', g2: '#10a0b4' },
    { l: '운영', i: 'monitor', d: '운영·A/S·유지관리', g1: '#19c08a', g2: '#12a874' },
  ];
  return (
    <main>
      <PageLoader />
      <SubPageHero title="도입 절차" subtitle="상담·서류검토·현장실사·설계·설치·연동·교육·A/S까지, EVS 도입의 전 과정을 단계별로 안내합니다." image={HERO} dim crumbs={[{ label: 'HOME', to: '/' }, { label: '설치안내', to: '/install/process' }, { label: '도입 절차' }]} />
      <section className={c.section}><div className={c.inner}><Reveal>
        <p className={c.eyebrow}>PROCESS</p><h2 className={c.headline}>한눈에 보는 도입 절차</h2>
        <p className={c.lead}>상담에서 운영까지, EVS가 전 과정을 함께합니다.</p>
        <div className={x.procGrid}>
          {steps.map((st, i) => (
            <div key={st.l} className={x.procStep} style={{ ['--g1' as string]: st.g1, ['--g2' as string]: st.g2 } as React.CSSProperties}>
              <div className={x.procHead}>
                <strong>STEP {String(i + 1).padStart(2, '0')}</strong>
                <span className={x.procRing}>{i + 1}/{steps.length}</span>
              </div>
              <div className={x.procBody}>
                <div className={x.procIco}>{GUIDE_ICON[st.i]}</div>
                <h3 className={x.procLabel}>{st.l}</h3>
                <p className={x.procDesc}>{st.d}</p>
              </div>
            </div>
          ))}
        </div>
      </Reveal></div></section>
      <section className={`${c.section} ${c.alt}`}><div className={c.inner}><Reveal>
        <p className={c.eyebrow}>DETAIL</p><h2 className={c.headline}>단계별 역할 · 예상 일정 · 완료 기준</h2>
        <div className={x.cardGrid}>
          <div className={x.card}><Ic name="doc" /><h3>단계별 설명</h3><p>각 단계에서 고객 준비사항과 EVS 수행사항을 구분해 안내합니다.</p></div>
          <div className={x.card}><Ic name="calendar" /><h3>예상 일정</h3><p>현장 규모·자재·통신 환경에 따라 일정이 달라집니다.</p></div>
          <div className={x.card}><Ic name="certified" /><h3>완료 기준</h3><p>장비 동작·통신·데이터·교육·문서 인계까지 확인합니다.</p></div>
        </div>
        <Soon label="표준 도입절차·역할표" />
      </Reveal></div></section>
      <section className={c.section}><div className={c.inner}><Reveal>
        <p className={c.eyebrow}>CONTACT</p><h2 className={c.headline}>도입을 시작하시려면</h2>
        <div className={x.ctaRow}><Button variant="pill" onClick={() => nav('/support/consult')}>도입 상담</Button><a className={x.ghost} onClick={() => nav('/install/site-survey')}>현장실사 안내</a></div>
      </Reveal></div></section>
    </main>
  );
};

// ── 5-2 현장실사·준비자료 ──
export const InstallSiteSurvey = () => {
  const nav = useNavigate();
  return (
    <main>
      <PageLoader />
      <SubPageHero title="현장실사·준비자료" subtitle="정확한 견적과 설계는 인허가 서류와 현장 조건 확인에서 시작됩니다. 실사 전 준비해야 할 자료를 안내합니다." image={HERO} dim crumbs={[{ label: 'HOME', to: '/' }, { label: '설치안내', to: '/install/process' }, { label: '현장실사·준비자료' }]} />
      <section className={c.section}><div className={c.inner}><Reveal>
        <p className={c.eyebrow}>DOCUMENTS</p><h2 className={c.headline}>준비 서류 · 현장 확인</h2>
        <div className={x.cardGrid}>
          <div className={x.card}><Ic name="doc" /><h3>준비 서류</h3><p>사업자등록·인허가/신고·시설명세·배치도·계획서.</p></div>
          <div className={x.card}><Ic name="building" /><h3>현장 확인</h3><p>시설 수량·설치 위치·전원·통신·배선 거리·환경.</p></div>
        </div>
        <p className={x.note}>※ 특수조건(고소·방폭·SUS·야간·보안·출입)은 상담 시 함께 확인합니다.</p>
      </Reveal></div></section>
      <section className={`${c.section} ${c.alt}`}><div className={c.inner}><Reveal>
        <p className={c.eyebrow}>RESULT</p><h2 className={c.headline}>실사 결과 · 신청</h2>
        <p className={c.lead}>실사 후 장비 구성·수량·포설 방법·통신 방식·견적 범위를 제안합니다.</p>
        <Soon label="실사 체크리스트·필요서류 양식" />
        <div className={x.ctaRow}><Button variant="pill" onClick={() => nav('/support/inquiry')}>현장실사 신청</Button><a className={x.ghost} onClick={() => nav('/support/consult')}>상담하기</a></div>
      </Reveal></div></section>
    </main>
  );
};

// ── 5-3 설치 후 운영 ──
export const InstallOperation = () => {
  const nav = useNavigate();
  return (
    <main>
      <PageLoader />
      <SubPageHero title="설치 후 운영" subtitle="설치가 끝이 아닙니다. 데이터 전송 상태와 장비를 지속 점검하고, A/S로 안정적인 운영을 함께 책임집니다." image={HERO} dim crumbs={[{ label: 'HOME', to: '/' }, { label: '설치안내', to: '/install/process' }, { label: '설치 후 운영' }]} />
      <section className={c.section}><div className={c.inner}><Reveal>
        <p className={c.eyebrow}>OPERATION</p><h2 className={c.headline}>인계 · 교육 · 점검 · 유지관리</h2>
        <div className={`${x.cardGrid} ${x.three}`}>
          <div className={x.card}><Ic name="doc" /><h3>인계자료</h3><p>설치내역·장비목록·사진·구성도·설명서.</p></div>
          <div className={x.card}><Ic name="action" /><h3>사용교육</h3><p>값 확인·경보·통신상태·보고서·문의 방법.</p></div>
          <div className={x.card}><Ic name="gauge" /><h3>일상점검</h3><p>전원·센서·통신·오염·소모품 확인.</p></div>
        </div>
        <div className={x.ctaRow} style={{ marginTop: 20 }}>
          <a className={x.ghost} onClick={() => nav('/support/resources')}>자료실</a>
          <a className={x.ghost} onClick={() => nav('/support/faq')}>FAQ</a>
        </div>
      </Reveal></div></section>
      <section className={`${c.section} ${c.alt}`}><div className={c.inner}><Reveal>
        <p className={c.eyebrow}>MAINTENANCE</p><h2 className={c.headline}>유지관리 · 장애 접수</h2>
        <p className={c.lead}>원격점검·방문점검(유상·무상 범위 구분) 후, 장비번호·증상·사진·수신상태로 A/S를 접수합니다.</p>
        <div className={x.ctaRow}><Button variant="pill" onClick={() => nav('/support/as')}>A/S 접수</Button><a className={x.ghost} onClick={() => nav('/support/consult')}>유지관리 상담</a></div>
      </Reveal></div></section>
    </main>
  );
};

// ── 4-3 IoT 설치사업 (/business/install) ──
// [2026-10-11 사용자] 환경(다크) 메뉴 소속인데 라이트(SubPageHero+Company)였던 것을 '다크로 통일'.
//   WHY: v7에서 설치사업을 환경 하위로 재귀속 → 테마/히어로가 환경 페이지(EnvironmentLanding·Greenlink)와 달라 이질감.
//   변경: SubPageHero(라이트) → s.hero(다크) + HeroCrumbs, c/x(라이트) → s/t(다크), 아이콘=IcD(t.cardIcon 시안), placeholder=SoonD(s.soon).
//   히어로 이미지=환경 통일 hero-guide. 콘텐츠·링크·문서근거(4-3)는 그대로 유지, 테마만 교체.
//   대안(라이트 유지) 거부: 같은 환경 메뉴 내 페이지 간 배경색/히어로 통일성 요구([[evs-hero-uniformity]])와 충돌.
export const BusinessInstall = () => {
  const nav = useNavigate();
  return (
    <main className={w.page}>
      <PageLoader />
      <header className={w.hero}>
        <div className={w.heroBg} style={{ backgroundImage: `url(${HERO_ENV})` }} />
        <div className={w.heroOverlay} />
        <HeroCrumbs items={['HOME', '환경', 'IoT 설치사업']} />
        <div className={w.heroInner}>
          <h1 className={w.heroTitle}>IoT 설치사업</h1>
          <p className={w.heroSub}>현장실사·설계·포설·설치·연동·교육·A/S까지, 대기배출 IoT 측정기기 설치의 전 과정을 EVS가 일괄 수행합니다.</p>
        </div>
      </header>

      {/* ① 서비스 범위 — ★유일 다이어그램: 셰브론(실사→설계→설치→교육 흐름) */}
      <section className={w.section}>
        <div className={w.inner}>
          <Reveal>
            <p className={w.eyebrow}>SERVICE SCOPE</p>
            <h2 className={w.h2}>서비스 범위</h2>
            <p className={w.lead}>서류검토·현장실사·설계·포설·설치·연동·교육·A/S까지 EVS가 일괄 수행합니다.</p>
            <div className={w.chevron}>
              {[
                { n: '01', t: '현장실사', i: 'building', d: '시설·전원·통신·거리·작업조건을 확인합니다.', c: w.cBlue },
                { n: '02', t: '설계·견적', i: 'solution', d: '장비 수량, 배선·SUS 등 포설, LTE·VPN 구성.', c: w.cTeal },
                { n: '03', t: '설치·시운전', i: 'signal', d: '장비 설치·계측값·통신·정상전송 확인.', c: w.cIndigo },
                { n: '04', t: '교육·인계', i: 'action', d: '사용 방법·장애 확인·제공 문서.', c: w.cCoral },
              ].map((item) => (
                <div key={item.n} className={`${w.chevronStep} ${item.c}`}>
                  <div className={w.chevronIco}>{GUIDE_ICON[item.i]}</div>
                  <span className={w.chevronNum}>{item.n}</span>
                  <h3>{item.t}</h3><p>{item.d}</p>
                </div>
              ))}
            </div>
            <a className={w.moreLink} onClick={() => nav('/install/process')}>도입 절차 보기 →</a>
          </Reveal>
        </div>
      </section>

      {/* ② 실적 · 사례 — 텍스트 + 준비중 */}
      <section className={`${w.section} ${w.alt}`}>
        <div className={w.inner}>
          <Reveal>
            <p className={w.eyebrow}>RECORD</p>
            <h2 className={w.h2}>실적 · 사례</h2>
            <p className={w.lead}>지역·업종·규모별 설치 실적을 보유하고 있습니다.</p>
            <SoonW label="설치 전후 사진·표준 설치절차" />
            <div className={w.ctaRow} style={{ justifyContent: 'flex-start', marginTop: 28 }}>
              <Button variant="pill" onClick={() => nav('/install/site-survey')}>현장실사 신청</Button>
              <a className={w.ctaGhost} onClick={() => nav('/support/inquiry')}>설치 견적</a>
              <a className={w.ctaGhost} onClick={() => nav('/cases?tab=environment')}>환경 사례</a>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
};
